import { Button, Text, TextInput } from "@marketplace/ui";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MdInfoOutline, MdRefresh } from "react-icons/md";

import { useGetCrmHierarchyMutation } from "../../hooks/queries/use-get-crm-hierarchy-mutation";
import type { CrmProductFormSchema } from "../../hooks/schemas/use-crm-product-form-schema";
import { FileXIcon } from "../../icons/file-x-icon";

export const CrmProductHierarchy = ({
  opened,
  isPending
}: {
  opened: boolean;
  isPending: boolean;
}) => {
  const { t } = useTranslation("product", {
    keyPrefix: "integrationProductListPage.crmModal"
  });

  const {
    register,
    watch,
    setValue,
    formState: { errors }
  } = useFormContext<CrmProductFormSchema>();

  const crmProductId = watch("crm_product_id");
  const formHierarchy1 = watch("hierarchy_name_level1");
  const formHierarchy2 = watch("hierarchy_name_level2");
  const formHierarchy3 = watch("hierarchy_name_level3");
  const formHierarchy4 = watch("hierarchy_name_level4");

  const {
    mutateAsync: fetchHierarchy,
    isPending: isHierarchyPending,
    data: hierarchyData,
    reset: resetHierarchy
  } = useGetCrmHierarchyMutation();

  const handleFetchHierarchy = async () => {
    if (!crmProductId) return;
    const res = await fetchHierarchy({ productId: crmProductId });
    if (res?.data) {
      const dataObj = Array.isArray(res.data) ? res.data[0] : res.data;
      if (dataObj) {
        setValue("hierarchy_name_level1", dataObj.hierarchy_name_level1 || "");
        setValue("hierarchy_name_level2", dataObj.hierarchy_name_level2 || "");
        setValue("hierarchy_name_level3", dataObj.hierarchy_name_level3 || "");
        setValue("hierarchy_name_level4", dataObj.hierarchy_name_level4 || "");
      }
    }
  };

  // Reset hierarchy when modal closes
  useEffect(() => {
    if (!opened) {
      resetHierarchy();
    }
  }, [opened, resetHierarchy]);

  return (
    <div className="flex flex-col gap-2">
      <TextInput
        {...register("crm_product_id")}
        size="lg"
        label={t("labels.crmProductId")}
        placeholder={t("placeholders.crmProductId")}
        error={errors.crm_product_id?.message}
        disabled={isPending}
        withAsterisk
        classNames={{
          input: "pr-[110px]",
          section: "w-auto mx-1"
        }}
        rightSection={
          <Button
            variant="primary"
            type="button"
            size="sm"
            onClick={handleFetchHierarchy}
            loading={isHierarchyPending}
            disabled={isPending || !crmProductId}
            className="w-auto md:w-[100px]"
            rightSection={
              isHierarchyPending ? undefined : <MdRefresh size={16} />
            }
          >
            {t("buttons.hierarchy")}
          </Button>
        }
      />

      <div className="border-solid border border-noble-blue-500 rounded-lg p-2 overflow-auto">
        <div className="flex gap-x-1 items-center mb-2">
          <MdInfoOutline size={14} className="text-noble-blue-500" />
          <Text size="xs">{t("labels.productHierarchy")}</Text>
        </div>
        <div className="flex rounded-lg overflow-hidden items-stretch">
          <div className="min-h-full w-1 bg-noble-blue-500" />
          <div className="flex-1 flex-col gap-[2px] p-1 bg-mischka-100 text-fjord-grey-400">
            {(() => {
              const renderData = hierarchyData
                ? Array.isArray(hierarchyData.data)
                  ? hierarchyData.data[0]
                  : hierarchyData.data
                : {
                    hierarchy_name_level1: formHierarchy1,
                    hierarchy_name_level2: formHierarchy2,
                    hierarchy_name_level3: formHierarchy3,
                    hierarchy_name_level4: formHierarchy4
                  };

              const isDataEmpty =
                !renderData ||
                Object.values(renderData).every(
                  (val) => val === "" || val === null || val === undefined
                );

              if (!isDataEmpty) {
                return Object.entries(renderData).map(([key, value]) => (
                  <Text type="paragraph" key={key}>
                    "{key}": "{String(value)}",
                  </Text>
                ));
              }

              return (
                <div className="flex flex-col items-center justify-center py-3 text-center w-full">
                  <FileXIcon className="mb-2" />
                  <Text className="text-fjord-grey-500 mb-[2px]">
                    {t("labels.emptyHierarchyTitle")}
                  </Text>
                  <Text size="xs" className="text-fjord-grey-400">
                    {t("labels.emptyHierarchyDesc")}
                  </Text>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};
